import { useState, useEffect, useRef, useCallback } from "react";
import type { TabId } from "../components/DetailPane/DetailTabs";
import type {
  WindowType,
  WindowRegistryEntry,
  PlaybackState,
  PlaybackCommandAction,
  WorkerOutboundMessage,
} from "../types/popout";
import { createPopoutWorker } from "../workers/createPopoutWorker";

// ---------------------------------------------------------------------------
// Singleton SharedWorker
// ---------------------------------------------------------------------------

let sharedWorkerInstance: SharedWorker | null = null;

function getSharedWorker(): SharedWorker {
  sharedWorkerInstance ??= createPopoutWorker();
  return sharedWorkerInstance;
}

/** Reset the module-level singleton. Exported for test isolation only. */
export function _resetSharedWorkerForTests(): void {
  sharedWorkerInstance = null;
}

// ---------------------------------------------------------------------------
// Window ID generator
// ---------------------------------------------------------------------------

export function generateWindowId(): string {
  return `win-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

// ---------------------------------------------------------------------------
// Hook options and return type
// ---------------------------------------------------------------------------

export interface UsePopoutManagerOptions {
  windowType: WindowType;
  interactionId: string;
  tabId?: TabId;
}

export interface UsePopoutManagerResult {
  windowId: string;
  windowRegistry: WindowRegistryEntry[];
  playbackState: PlaybackState | null;
  playerClosed: boolean;
  isPopped: (id: string) => boolean;
  getDetachedTabs: (id: string) => Set<TabId>;
  sendCommand: (id: string, command: PlaybackCommandAction) => void;
  broadcastPlayback: (id: string, state: PlaybackState) => void;
  notifyDetachTab: (id: string, tab: TabId) => void;
  notifyReattachTab: (id: string, tab: TabId) => void;
  setOnPlaybackCommand: (fn: (command: PlaybackCommandAction) => void) => void;
  unregister: () => void;
}

// ---------------------------------------------------------------------------
// Hook implementation
// ---------------------------------------------------------------------------

export function usePopoutManager(
  options: UsePopoutManagerOptions
): UsePopoutManagerResult {
  const { windowType, interactionId, tabId } = options;

  // Stable window ID — generated once per hook instance
  const windowIdRef = useRef<string>(generateWindowId());

  // Port reference to the SharedWorker
  const portRef = useRef<MessagePort | null>(null);

  // Guard to ensure UNREGISTER is only sent once (public unregister() and
  // useEffect cleanup can both fire for the same windowId).
  const unregisteredRef = useRef(false);

  // Callback ref for PLAYBACK_COMMAND messages received by this window
  const onPlaybackCommandRef = useRef<((command: PlaybackCommandAction) => void) | null>(null);

  // React state
  const [windowRegistry, setWindowRegistry] = useState<WindowRegistryEntry[]>([]);
  const [playbackState, setPlaybackState] = useState<PlaybackState | null>(null);
  const [detachedTabsMap, setDetachedTabsMap] = useState<Record<string, Set<TabId>>>({});
  const [playerClosed, setPlayerClosed] = useState(false);

  // ---------------------------------------------------------------------------
  // Connect to SharedWorker and manage lifecycle
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const worker = getSharedWorker();
    const port = worker.port;
    portRef.current = port;

    // Message handler
    port.onmessage = (event: MessageEvent<WorkerOutboundMessage>) => {
      const msg = event.data;
      switch (msg.type) {
        case "STATE_SYNC": {
          setWindowRegistry(msg.windows);
          if (msg.playback[interactionId]) {
            setPlaybackState(msg.playback[interactionId]);
          }
          // Convert detachedTabs arrays to Sets
          const newMap: Record<string, Set<TabId>> = {};
          Object.entries(msg.detachedTabs).forEach(([id, tabs]) => {
            newMap[id] = new Set(tabs);
          });
          setDetachedTabsMap(newMap);
          break;
        }

        case "WINDOW_REGISTRY_UPDATE": {
          setWindowRegistry(msg.windows);
          break;
        }

        case "PLAYBACK_STATE": {
          if (msg.interactionId === interactionId) {
            setPlaybackState({
              currentTime: msg.currentTime,
              duration: msg.duration,
              isPlaying: msg.isPlaying,
              playbackRate: msg.playbackRate,
            });
          }
          break;
        }

        case "TAB_STATE_UPDATE": {
          setDetachedTabsMap((prev) => ({
            ...prev,
            [msg.interactionId]: new Set(msg.detachedTabs),
          }));
          break;
        }

        case "PLAYER_CLOSED": {
          if (msg.interactionId === interactionId) {
            setPlayerClosed(true);
          }
          break;
        }

        case "PLAYBACK_COMMAND": {
          if (msg.interactionId === interactionId && onPlaybackCommandRef.current) {
            onPlaybackCommandRef.current(msg.command);
          }
          break;
        }

        default:
          break;
      }
    };

    // Start port (required for SharedWorker MessagePort)
    port.start();

    // Register this window with the worker
    port.postMessage({
      type: "REGISTER",
      windowId: windowIdRef.current,
      windowType,
      interactionId,
      tabId,
    });

    // Copy ref value for cleanup (react-hooks/exhaustive-deps)
    const currentWindowId = windowIdRef.current;

    // Cleanup: unregister on unmount (guard against double-send with public unregister())
    return () => {
      if (!unregisteredRef.current) {
        unregisteredRef.current = true;
        port.postMessage({
          type: "UNREGISTER",
          windowId: currentWindowId,
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interactionId, windowType, tabId]);

  // ---------------------------------------------------------------------------
  // API methods
  // ---------------------------------------------------------------------------

  const isPopped = useCallback(
    (id: string): boolean =>
      windowRegistry.some((w) => w.windowType === "popout" && w.interactionId === id),
    [windowRegistry]
  );

  const getDetachedTabs = useCallback(
    (id: string): Set<TabId> => detachedTabsMap[id] ?? new Set<TabId>(),
    [detachedTabsMap]
  );

  const sendCommand = useCallback(
    (id: string, command: PlaybackCommandAction): void => {
      portRef.current?.postMessage({
        type: "PLAYBACK_COMMAND",
        interactionId: id,
        command,
      });
    },
    []
  );

  const broadcastPlayback = useCallback(
    (id: string, state: PlaybackState): void => {
      portRef.current?.postMessage({
        type: "PLAYBACK_UPDATE",
        interactionId: id,
        currentTime: state.currentTime,
        duration: state.duration,
        isPlaying: state.isPlaying,
        playbackRate: state.playbackRate,
      });
    },
    []
  );

  const notifyDetachTab = useCallback((id: string, tab: TabId): void => {
    portRef.current?.postMessage({
      type: "DETACH_TAB",
      interactionId: id,
      tabId: tab,
    });
  }, []);

  const notifyReattachTab = useCallback((id: string, tab: TabId): void => {
    portRef.current?.postMessage({
      type: "REATTACH_TAB",
      interactionId: id,
      tabId: tab,
    });
  }, []);

  const setOnPlaybackCommand = useCallback(
    (fn: (command: PlaybackCommandAction) => void): void => {
      onPlaybackCommandRef.current = fn;
    },
    []
  );

  const unregister = useCallback((): void => {
    if (!unregisteredRef.current) {
      unregisteredRef.current = true;
      portRef.current?.postMessage({
        type: "UNREGISTER",
        windowId: windowIdRef.current,
      });
    }
  }, []);

  return {
    windowId: windowIdRef.current,
    windowRegistry,
    playbackState,
    playerClosed,
    isPopped,
    getDetachedTabs,
    sendCommand,
    broadcastPlayback,
    notifyDetachTab,
    notifyReattachTab,
    setOnPlaybackCommand,
    unregister,
  };
}
